import { useEffect, useMemo, useState } from "react";
import Loader from "../../../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Box, Checkbox, Typography } from "@mui/material";
import {
  updateVoucherPaymentData,
  setShowThankYouMessage,
  setisUpgradePreferentialStep,
  setActiveStep,
  getVoucherById,
  setEditPreviewApplicant,
} from "../voucher-form.slice";
import * as yup from "yup";
import BankDetailsTable from "./payment-options/BankDetailsTable";
import { useFormik } from "formik";
import CommonButtonVoucherForm from "../../../shared-components/common-button/CommonButtonVoucherForm";
import { route } from "../../../services/apiRoutes";
import TermsAndConditionsModal from "./TermsAndConditionsModal";
import MultiTransaction from "./payment-options/PaymentOptionsForm";
import CancellationDetails from "./payment-options/CancellationDetails";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import {
  PaymentbuildPayload,
  setFormValuesFromApplicantData,
  formatIndianNumber,
} from "./common-functions/PaymentCommonFunctions";
import { EOIFinanceStatus, EOIPaymentMode, FORM_PHASE } from "../../../config/constant";
const PaymentOptions = () => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [termsAndConditionModal, setTermsAndConditionModal] = useState(false);
  const [snackbarConfig, setSnackBarconfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const dispatch = useDispatch();
  const { voucherId } = useParams();
  const [totalAmountPayable, setTotalAmountPayable] = useState("");

  const { applicantData, activeStep, isLoading, isUpgradePreferentialStep, eoiCampaignDetails } =
    useSelector((state) => state?.voucherForm);

  const originalTransactions = useMemo(() => {
    return applicantData?.payments?.map(tx => ({
      id: tx.id,
      paymentMethod: tx.paymentDetails?.method || "",
      offlineRadioOptions: tx.paymentDetails?.method || "",
      chequeDDNumber: tx.paymentDetails?.chequeNumber || "",
      transactionNumber: tx.paymentDetails?.transactionNumber || "",
      bankName: tx.paymentDetails?.drawnOn || "",
      date: tx.date || "",
      amount: String(tx.paidAmount || ""),
      paymentProof: tx.paymentDetails?.paymentProof?.[0] || null,
    })) || [];
  }, [applicantData?.payments]);

  const termsAndConditionsdData =
    eoiCampaignDetails?.eoiTermsAndCondition ||
    eoiCampaignDetails?.voucherTermsAndCondition;

  const paymentOptionsFormik = useFormik({
    initialValues: {
      cancellationPayeename: "",
      cancellationBankname: "",
      cancellationIfscCode: "",
      cancellationAccountNo: "",
      cancellationAccountType: "",
      cancelledCheque: [],
      isPhysicalCancelledCheque: false,
      termsAndConditions: false,
    },
    validationSchema: yup.object().shape({
      // Radio button
      // Cancellation/Refund shared fields across all payment methods
      cancellationPayeename: yup
        .string()
        .matches(/^[A-Za-z ]+$/, "Payee name must contain only letters")
        .required("Payee name is required"),
      cancellationBankname: yup
        .string()
        .matches(/^[A-Za-z ]+$/, "Bank name must contain only letters")
        .required("Bank name is required"),
      cancellationIfscCode: yup
        .string()
        .matches(
          /^[A-Z]{4}0[A-Z0-9]{6}$/,
          "Enter a valid IFSC code (eg. SBIN0001537)"
        )
        .required("IFSC code is required"),
      cancellationAccountNo: yup
        .string()
        .matches(
          /^\d{9,}$/,
          "Account number must be numeric and at least 9 digits"
        )
        .required("Account number is required"),
      cancellationAccountType: yup
        .string()
        .required("Account type is required"),
      cancelledCheque: yup.array().when("isPhysicalCancelledCheque", {
        is: false,
        then: (schema) =>
          schema
            .min(1, "Please upload Cancelled Cheque or Bank Statement")
            .required("Cancelled Cheque or Bank Statement is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      // Terms
      termsAndConditions: yup
        .boolean()
        .oneOf([true], "You must agree to the Terms & Conditions"),
    }),
    onSubmit: (values) => {
      //console.log("Form Values:", values);
      // dispatch API call or submit handler
    },
  });

  const MultipaymentOptionsFormik = useFormik({
    initialValues: {
      transactions: [
        {
          id: "",
          paymentMethod: "", // Online | Offline
          offlineRadioOptions: "",
          bankName: "",
          branchName: "",
          accountNo: "",
          amount: "",
          paymentProof: [],
          status: EOIFinanceStatus.UNVERIFIED,
          date: null,
          type: "",
          isPaid: false,
          showUploadPopup: false,
          isPhysicalPaymentProof: false,
          email: "",
          onlineMethod: "",
          contact: "",
          orderId: "",
          gatewayPaymentId: "",
          paymentTransactionId: "",
          paidAmount: "",
        },
      ],
    },

    validationSchema: yup.object().shape({
      transactions: yup.array().of(
        yup.object().shape({
          paymentMethod: yup.string().required("Payment method is required"),

          // Always required
          amount: yup
            .number()
            .typeError("Amount must be numeric")
            .required("Amount is required")
            .moreThan(0, "Amount must be greater than 0"),

          isPaid: yup.boolean().when("paymentMethod", {
            is: EOIPaymentMode.GATEWAY,
            then: (schema) =>
              schema.oneOf([true], "Online Payment needs to be paid to submit"),
            otherwise: (schema) => schema.notRequired(),
          }),

          // Offline-specific validations
          offlineRadioOptions: yup.string().when("paymentMethod", {
            is: EOIPaymentMode.OFFLINE,
            then: (schema) =>
              schema.required("Please select an offline option"),
            otherwise: (schema) => schema.notRequired(),
          }),

          transactionNo: yup
            .string()
            .when(["paymentMethod", "offlineRadioOptions"], {
              is: (paymentMethod, offlineRadioOptions) =>
                paymentMethod === EOIPaymentMode.OFFLINE &&
                offlineRadioOptions === "CHEQUE",
              then: (schema) =>
                schema
                  .required("Cheque/DD number is required")
                  .matches(
                    /^\d{6}$/,
                    "Cheque/DD number must be exactly 6 digits"
                  ),
              otherwise: (schema) =>
                schema.when("paymentMethod", {
                  is: EOIPaymentMode.OFFLINE,
                  then: (s) =>
                    s
                      .required("Transaction ID is required"),
                  otherwise: (s) => s.notRequired(),
                }),
            }),

          bankName: yup
            .string()
            .when(["paymentMethod", "offlineRadioOptions"], {
              is: (paymentMethod, offlineRadioOptions) =>
                paymentMethod === EOIPaymentMode.OFFLINE &&
                offlineRadioOptions === "CHEQUE",
              then: (schema) =>
                schema
                  .required("Bank name is required")
                  .matches(
                    /^[A-Za-z ]+$/,
                    "Bank Name must contain only letters"
                  ),
              otherwise: (schema) => schema.notRequired(),
            }),

          branchName: yup.string().notRequired(),
          accountNo: yup.string().notRequired(),

          // accountNo: yup.string().when("paymentMethod", {
          //   is: EOIPaymentMode.OFFLINE,
          //   then: (schema) =>
          //     schema
          //       .matches(/^\d{9,}$/, "Account number must be at least 9 digits")
          //       .required("Account number is required"),
          //   otherwise: (schema) => schema.notRequired(),
          // }),

          paymentProof: yup
            .array()
            .when(["paymentMethod", "isPhysicalPaymentProof"], {
              is: (paymentMethod, isPhysicalPaymentProof) =>
                paymentMethod === EOIPaymentMode.OFFLINE &&
                isPhysicalPaymentProof === false,
              then: (schema) =>
                schema
                  .min(1, "Payment proof is required")
                  .required("Payment proof is required"),
              otherwise: (schema) => schema.notRequired(),
            }),

          date: yup.date().when("paymentMethod", {
            is: EOIPaymentMode.OFFLINE,
            then: (schema) => schema.required("Date is required"),
            otherwise: (schema) => schema.notRequired(),
          }),
        })
      ),
    }),

    onSubmit: (values) => {
      //console.log("Form Values:", values);
      // dispatch API call or submit handler
    },
  });

  useEffect(() => {
    paymentOptionsFormik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentOptionsFormik?.values]);

  const handlePrevious = () => {
    dispatch(setActiveStep(activeStep - 1));
  };

  // Helper function to create the other details payload

  useEffect(() => {
    paymentOptionsFormik.setErrors({});
    paymentOptionsFormik.setTouched({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentOptionsFormik.values.paymentMethod]);

  // const deepTouch = (obj) => {
  //   if (Array.isArray(obj)) {
  //     return obj.map((item) => deepTouch(item));
  //   } else if (obj && typeof obj === "object") {
  //     return Object.fromEntries(
  //       Object.keys(obj).map((key) => [key, deepTouch(obj[key])])
  //     );
  //   } else {
  //     return true;
  //   }
  // };

  // Function to build final payload
  const handleSaveForm = async () => {
    // ✅ Validate both forms
    const multiErrors = await MultipaymentOptionsFormik.validateForm();
    const cancelErrors = await paymentOptionsFormik.validateForm();

    // ✅ If errors exist, mark fields as touched so error messages show
    if (
      Object.keys(multiErrors).length > 0 ||
      Object.keys(cancelErrors).length > 0
    ) {
      // 🔹 Proper nested touched for multipayment form
      const touchedTransactions =
        MultipaymentOptionsFormik.values.transactions.map((tx) =>
          Object.fromEntries(
            Object.keys(tx).map((k) => [k, true]) // mark each field in transaction as touched
          )
        );

      MultipaymentOptionsFormik.setTouched({
        transactions: touchedTransactions,
      });

      // 🔹 Touch cancel form fields
      paymentOptionsFormik.setTouched(
        Object.fromEntries(
          Object.keys(paymentOptionsFormik.values).map((k) => [k, true])
        )
      );

      return; // ❌ Stop here, don’t submit payload
    }

    setIsSubmitDisabled(true);
    try {
      const payload = PaymentbuildPayload(
        MultipaymentOptionsFormik.values,
        paymentOptionsFormik.values,
        totalAmountPayable,
        false,
        activeStep,
        originalTransactions,
      );

      const res = await dispatch(
        updateVoucherPaymentData({
          path: `${route.VOUCHER_UPDATE_PAYMENT_DATA}/${voucherId}`,
          payload,
          opportunityId: voucherId,
        })
      ).unwrap();

      // Navigate only on success response
      if (res?.status === 200 || res?.status === 201) {
        dispatch(setShowThankYouMessage(true));
        dispatch(setisUpgradePreferentialStep(false));
        dispatch(setActiveStep(0))
        dispatch(setEditPreviewApplicant(false))
        window.location.href = `/voucher-form/thank-you/${voucherId}`;
        return;
      }

      // If not successful, show error instead of success
      setSnackBarconfig({
        open: true,
        message: res?.message || "Failed to update payment details",
        severity: "error",
      });
    } catch (error) {
      setSnackBarconfig({
        open: true,
        message: error?.errors?.message || "Failed to update payment details",
        severity: "error",
      });
      console.error("Payment update failed:", error);
    } finally {
      setIsSubmitDisabled(false);
    }
  };

  useEffect(() => {
    if (voucherId) {
      dispatch(getVoucherById(`/${voucherId}`));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherId]);
  
  useEffect(() => {
    if (applicantData) {
      setFormValuesFromApplicantData(
        applicantData.paymentDetails,
        applicantData?.payments,
        MultipaymentOptionsFormik,
        paymentOptionsFormik,
        applicantData.isAgreedOnTerms
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData]);

  const handleSnackbarClose = () => {
    setSnackBarconfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  const getPayableAmount = () => {
    if (!applicantData || !eoiCampaignDetails) return "0";

    const { formPhase, eoiDetails } = applicantData;
    const eoiType = eoiDetails?.eoiType;

    if (formPhase === FORM_PHASE.VOUCHER) {
      if (!isUpgradePreferentialStep) return eoiCampaignDetails?.voucherAmount || "0";
    }
    if (formPhase === FORM_PHASE.EOI) {
      if (eoiType === "Preferential") return eoiCampaignDetails?.preEoiAmount || "0";
      if (eoiType === "Standard") return eoiCampaignDetails?.stdEoiAmount || "0";
    }

    return eoiCampaignDetails?.preEoiAmount || eoiCampaignDetails?.stdEoiAmount || eoiCampaignDetails?.voucherAmount ; // default fallback
  };

  const getTitle = () => {
    if (!applicantData) return "Voucher Value";

    const { formPhase } = applicantData;

    if (formPhase === FORM_PHASE.VOUCHER) {
      if (!isUpgradePreferentialStep) return "Voucher Value";
    }

    if (formPhase === FORM_PHASE.EOI) {
      return "EOI Value";
    }

    return "Voucher Value"; // default fallback
  };

  useEffect(() => {
    const totalAmount = getPayableAmount();
    setTotalAmountPayable(totalAmount);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {isLoading && <Loader isLoading={isLoading} />}

      <Box
        my={2}
        py={1}
        px={3}
        sx={{
          width: "fit-content",
          margin: "12px auto",
          border: "1px solid rgba(26, 64, 125, 0.3)",
          borderRadius: "16px",
          textAlign: "center",
          boxShadow: "rgba(26, 64, 125, 0.3)",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ color: "#1A407D", fontWeight: 500 }}
        >
          {getTitle()}
        </Typography>
        <Typography
          sx={{ fontSize: "18px", fontWeight: 600, color: "#1C252E" }}
        >
          ₹{formatIndianNumber(getPayableAmount())}
        </Typography>
      </Box>

      <Typography
        sx={{
          color: "#1C252E",
          fontSize: "16px",
          fontWeight: "600",
          lineHeight: "36px",
          mb: "12px",
          textAlign: "center",
        }}
      >
        Payment Options
      </Typography>

      <form
        onSubmit={paymentOptionsFormik.handleSubmit}
        onReset={paymentOptionsFormik.handleReset}
      >
        <MultiTransaction paymentOptionsFormik={MultipaymentOptionsFormik} />
        <BankDetailsTable />
        <CancellationDetails paymentOptionsFormik={paymentOptionsFormik} />

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Checkbox
            sx={{ pl: 0 }}
            name="termsAndConditions"
            checked={paymentOptionsFormik.values.termsAndConditions}
            onChange={paymentOptionsFormik.handleChange}
            onBlur={paymentOptionsFormik.handleBlur}
          />
          <Typography variant="body2">
            I Agree to the{" "}
            <span
              style={{
                color: "#1A407D",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => setTermsAndConditionModal(true)}
            >
              Terms & Conditions
            </span>
          </Typography>
        </Box>
        {paymentOptionsFormik.touched.termsAndConditions &&
          paymentOptionsFormik.errors.termsAndConditions && (
            <Typography variant="caption" color="error">
              {paymentOptionsFormik.errors.termsAndConditions}
            </Typography>
          )}
        <TermsAndConditionsModal
          termsAndConditionModal={termsAndConditionModal}
          setTermsAndConditionModal={setTermsAndConditionModal}
          termsAndConditionsData= {termsAndConditionsdData}
        />

        <CommonButtonVoucherForm
          isSubmit
          handleNext={handleSaveForm}
          isDisable={isSubmitDisabled}
          handlePrevious={handlePrevious}
          saveDraftPath={`${route.VOUCHER_UPDATE_PAYMENT_DATA}/${voucherId}`}
          saveDraftData={{
            ...PaymentbuildPayload(
              MultipaymentOptionsFormik.values,
              paymentOptionsFormik.values,
              totalAmountPayable,
              true,
              activeStep,
              originalTransactions,
            ),
            lastStep:
              applicantData?.lastStep > activeStep
                ? applicantData?.lastStep
                : activeStep,
          }}
        />

        <SnackbarAlert
          open={snackbarConfig.open}
          message={snackbarConfig.message}
          severity={snackbarConfig.severity}
          onClose={handleSnackbarClose}
        />
      </form>
    </div>
  );
};

export default PaymentOptions;
